import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import App from './App';
import { AdminAuthProvider } from './contexts/AdminAuthContext';
import { DataCacheProvider } from './contexts/DataCacheContext';
import { Toaster } from 'sonner';
import EnvironmentLabel from './components/EnvironmentLabel';
import { initConsoleRedactor } from './lib/consoleRedactor';
import { initSentry } from './lib/sentry';
import { initFaro } from './lib/faro';
import './index.css';

// Initialize console redactor FIRST to mask infrastructure URLs (e.g., Supabase project IDs)
// This MUST run before any other code to catch all console output
initConsoleRedactor();

// GPC (Global Privacy Control): honor the browser signal
const gpcEnabled = navigator.globalPrivacyControl === true;
initSentry(gpcEnabled ? { gpc: true } : undefined);
if (!gpcEnabled) {
  initFaro();
}

/**
 * Error Fallback Component for Admin Panel
 */
const ErrorFallback = ({ error, componentStack, resetError }) => (
  <div className="flex items-center justify-center min-h-screen bg-gray-100">
    <div className="max-w-md p-8 bg-white rounded-lg shadow-xl border border-red-200">
      <div className="flex items-center mb-4">
        <svg
          className="w-8 h-8 text-red-500 mr-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <h1 className="text-xl font-bold text-gray-900">Admin Panel Error</h1>
      </div>

      <p className="text-gray-600 mb-4">
        Something unexpected happened. Our team has been notified.
      </p>

      {import.meta.env.VITE_ENVIRONMENT === 'development' && (
        <details className="mb-4">
          <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
            Error Details (Development Only)
          </summary>
          <pre className="mt-2 p-3 bg-gray-100 rounded text-xs overflow-auto max-h-40">
            {error?.toString()}
            {componentStack}
          </pre>
        </details>
      )}

      <div className="flex gap-3">
        <button
          onClick={resetError}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Try Again
        </button>
        <button
          onClick={() => window.location.href = '/login'}
          className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Back to Login
        </button>
      </div>
    </div>
  </div>
);

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={ErrorFallback} showDialog>
      <BrowserRouter>
        <AdminAuthProvider>
          <DataCacheProvider>
            <App />
            <Toaster position="top-right" richColors />
            <EnvironmentLabel />
          </DataCacheProvider>
        </AdminAuthProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);
